import { Navigate } from 'react-router';

import { FlagType } from '~/flags/flags';
import { useFlag } from '~/flags/useFlag';
import { RoutePathType } from '~/routing/enums';
import { useRouting } from '~/routing/useRouting';

const CUSTOM_AI_ROOT = '/custom-ai';

/**
 * "Automation Hub" briefly moved to `/intelligence` before that name was
 * dropped in favor of "Custom AI". Keeps old bookmarks and shared links
 * working by forwarding everything under the retired prefix — path and query
 * string included — to whichever of `/automation`/`/x-ray/library` or
 * `/custom-ai` is current. Unlike the legacy `/automation` tree, the
 * `/custom-ai` tree (EKP-64013) flattens Quick AI and Data Extraction out from
 * under the bare root and `contract-data`, so each segment is remapped
 * explicitly rather than passed through unchanged.
 */
export function LegacyIntelligenceRouteRedirect() {
  const { params, location } = useRouting();
  const hasStdAiUrlMigration = useFlag(FlagType.StdAiUrlMigration);
  const rest = params['*'] ?? '';

  const libraryMatch = rest.match(/^library(\/.*)?$/);
  if (libraryMatch) {
    const base = hasStdAiUrlMigration
      ? RoutePathType.CustomAiLibrary
      : RoutePathType.XRayLibrary;
    const to = `${base}${libraryMatch[1] ?? ''}${location.search}`;
    return <Navigate replace to={to} />;
  }

  if (hasStdAiUrlMigration) {
    if (!rest) {
      return (
        <Navigate
          replace
          to={`${RoutePathType.CustomAiHub}${location.search}`}
        />
      );
    }

    const dataExtractionMatch = rest.match(
      /^contract-data\/dynamic-data-extraction(\/.*)?$/,
    );
    if (dataExtractionMatch) {
      const to = `${RoutePathType.CustomAiDataExtraction}${dataExtractionMatch[1] ?? ''}${location.search}`;
      return <Navigate replace to={to} />;
    }

    return (
      <Navigate replace to={`${CUSTOM_AI_ROOT}/${rest}${location.search}`} />
    );
  }

  const remapped = rest
    .replace(
      /^contract-data\/dynamic-data-extraction(\/|$)/,
      'dynamic-data-extraction$1',
    )
    .replace(/^contract-data(\/|$)/, 'fields$1');

  const to = remapped
    ? `${RoutePathType.AutomationHub}/${remapped}${location.search}`
    : `${RoutePathType.AutomationHub}${location.search}`;

  return <Navigate replace to={to} />;
}
